import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { duration } from "@formant/core/model/duration";
import { IFilter } from "@formant/core/model/IFilter";
import { Uuid } from "@formant/core/model/Uuid";

import { track } from "../../../analytics";
import {
    recordVideoEnabled,
    teleopApp,
    terminalEnabled,
} from "../../../config";
import {
    appStore,
    deviceConfigurationStore,
    deviceDetailsStore,
    deviceStore,
    observeStore,
    operateStore,
    organizationStore,
    routerStore,
    rtcSignalingStore,
    screenRecordingStore,
    signalingStore,
} from "../../../stores";
import { deviceIsTeleopable } from "../../../utils/deviceIsTeleopable";
import { FeatureFlagLocalStorageManager } from "../../../utils/FeatureFlagLocalStorageManager/FeatureFlagLocalStorageManager";
import { FeatureFlagsLocalStorageEnum } from "../../../utils/FeatureFlagLocalStorageManager/FeatureFlagsLocalStorage.enum";
import { formatDateTimeLocaleISO } from "../../../utils/formatDateTimeLocaleISO";
import { IMenuItem } from "../Menu/IMenuItem";
import { MenuButton } from "../MenuButton";
import { RestrictedList } from "../RestrictedList";
import { Timer } from "../Timer";
import * as styles from "./index.scss";

export interface IDeviceMenuProps {
    deviceId: Uuid;
    mode: "observe" | "operate";
    onAnnotate?: () => void;
    onFullscreen?: () => void;
    showingFilters?: boolean;
    onToggleFilters?: () => void;
    filter?: IFilter;
    hasTimeline?: boolean;
}

@observer
export class DeviceMenu extends Component<IDeviceMenuProps> {
    private isObserve = this.props.mode === "observe";
    private isOperate = this.props.mode === "operate";
    private restrictedList = new RestrictedList<IMenuItem<string>>();

    @observable
    private menuOpened: boolean = false;

    public componentWillUnmount() {
        const { isRecording } = screenRecordingStore;
        if (isRecording) {
            screenRecordingStore.stopRecordingVideo();
        }
    }

    public render() {
        const { isObserve, isOperate, menuOpened } = this;
        const { share } = operateStore;
        const { hasTimeline, onFullscreen, onToggleFilters } = this.props;

        const items: IMenuItem<string>[] = share
            ? [...this.commandItem(1)]
            : [
                  ...this.configureDeviceItem(1),

                  ...[
                      ...this.teleopItem(1),
                      ...(onFullscreen ? this.fullscreenItem(1) : []),
                      ...(onToggleFilters ? this.toggleFiltersItem(1) : []),
                      ...this.terminalItem(2),
                      ...this.fitFillItem(2),
                      ...this.fleetSettings(2),
                      ...[
                          ...(hasTimeline
                              ? [
                                    ...this.commentItem(2),
                                    ...this.annotateItem(2),
                                    ...this.onDemandItem(2),
                                    ...this.commandItem(2),
                                    ...this.recordingItem(3),
                                    ...this.shareItem(3),
                                    ...this.exportItem(3),
                                ]
                              : []),
                      ],
                      ...this.metricsItem(3),
                      ...this.teleopPrimarySwap(3),
                      ...this.shareFeedbackItem(3),
                  ],
              ];

        if (items.length === 0) {
            return false;
        }

        return (
            <>
                <MenuButton
                    items={items}
                    header={isObserve}
                    className={styles.menu}
                    buttonClassName={classnames(
                        styles.button,
                        isOperate && styles.operate,
                    )}
                    bottom={isOperate}
                    onChanged={this.onMenuChanged}
                    data-testid="device-menu-button"
                />
                {isObserve && !menuOpened && (
                    // Don't run this while menu is open to avoid teleop option popping in
                    // and bumping everything down
                    <Timer
                        onTick={this.onTick}
                        interval={10 * duration.second}
                    />
                )}
            </>
        );
    }

    private onMenuChanged = (openState: boolean) => {
        this.menuOpened = openState;
    };

    private onTick = async () => {
        await rtcSignalingStore.fetchSessions();
        await rtcSignalingStore.fetchPeers();
        await signalingStore.fetchPeers();
    };

    private configureDeviceItem(group?: number): IMenuItem<string>[] {
        return this.restrictedList.new({
            items: [
                {
                    value: "Configure device",
                    icon: "settings",
                    group,
                    onSelected: this.onConfigureDevice,
                    hotkey: "shift+,",
                },
            ],
            resource: "devices",
            requiredAccess: "administrator",
        });
    }

    private onConfigureDevice = async () => {
        const { isObserve, isOperate } = this;
        const { deviceId } = this.props;
        const { navigate, routeTo } = routerStore;

        if (isObserve) {
            await navigate(
                routeTo.editDevice({
                    id: deviceId,
                }),
            );
        }

        if (isOperate) {
            const id = operateStore.session?.deviceId;
            if (id) {
                await navigate(
                    routeTo.editDevice({ id }, { tab: "Teleoperation" }),
                );
            }
        }
    };

    private commentItem(group?: number): IMenuItem<string>[] {
        const { onComment } = this;
        return this.restrictedList.new({
            items: [
                {
                    value: "Add a comment",
                    icon: "comment",
                    group,
                    onSelected: onComment,
                    hotkey: "shift+c",
                },
            ],
            resource: "comments",
            requiredAccess: "operator",
            requiredFeature: "share",
        });
    }

    private onComment = () => {
        const { deviceId } = this.props;
        const comment = { deviceId };
        observeStore.editComment(comment);
    };

    private annotateItem(group?: number): IMenuItem<string>[] {
        const { deviceId, onAnnotate } = this.props;
        const device = deviceDetailsStore.get(deviceId);

        return this.restrictedList.new({
            items: [
                {
                    value: "Annotate",
                    icon: "annotation",
                    group,
                    onSelected: onAnnotate,
                    hotkey: "shift+a",
                },
            ],
            resource: "annotations",
            requiredAccess: "operator",
            disabled: !device || !this.isObserve || !onAnnotate,
        });
    }

    private onDemandItem(group?: number): IMenuItem<string>[] {
        return this.restrictedList.new({
            items: [
                {
                    value: "Retrieve on-demand data",
                    icon: "on-demand",
                    group,
                    onSelected: this.onRetrieveOnDemandData,
                    hotkey: "shift+r",
                },
            ],
            resource: "commands",
            requiredAccess: "operator",
            requiredFeature: "onDemand",
            disabled: !this.isObserve,
        });
    }

    private onRetrieveOnDemandData = () => {
        const { deviceId } = this.props;
        const { focusedStreamName } = observeStore;
        const deviceConfiguration = deviceId
            ? deviceConfigurationStore.get(deviceId)
            : undefined;
        const isOnDemand = new Set(
            (
                (deviceConfiguration &&
                    deviceConfiguration.document.telemetry?.streams) ||
                []
            )
                .filter(_ => _.onDemand)
                .map(_ => _.name),
        );
        appStore.onDemandData(
            (focusedStreamName &&
                isOnDemand.has(focusedStreamName) &&
                focusedStreamName) ||
                "",
        );
    };

    private commandItem(group?: number): IMenuItem<string>[] {
        const { onIssueCommand } = this;
        return this.restrictedList.new({
            items: [
                {
                    value: "Issue command",
                    icon: "command",
                    group,
                    onSelected: onIssueCommand,
                    hotkey: "shift+i",
                },
            ],
            resource: "commands",
            requiredAccess: "operator",
            requiredFeature: "commands",
        });
    }

    private onIssueCommand = () => {
        deviceStore.issuingCommand = true;
    };

    private teleopItem(group?: number): IMenuItem<string>[] {
        const { deviceId } = this.props;
        const { isObserve, onNavigateToTeleop } = this;

        const organization = organizationStore.authenticated();
        const teleop3PreviewButton =
            (organization && organization.teleop3PreviewButton) ||
            FeatureFlagLocalStorageManager.getFlag(
                FeatureFlagsLocalStorageEnum.EnableTeleop3PreviewButton,
            );

        return this.restrictedList.new({
            items: [
                {
                    icon: "teleop",
                    value: "Teleop device",
                    group,
                    onSelected: onNavigateToTeleop,
                    hotkey: "shift+t",
                },
                ...(teleop3PreviewButton
                    ? [
                          {
                              icon: "joystick" as any,
                              value: "Teleop device (preview)",
                              group,
                              onSelected: () => {
                                  // Open up teleop in a new tab. Navigate only;
                                  // do not autoConnect (DEV-17281: auto-connecting
                                  // on page load raced teleop's session-state poll
                                  // and left the device rendered as Offline).
                                  // Link to the device, not a view (DEV-17392):
                                  // teleop resolves the device's current default
                                  // view itself, so the link survives views being
                                  // renamed, deleted or changed.
                                  const refreshToken =
                                      localStorage.getItem("refreshToken");
                                  if (refreshToken) {
                                      const refreshTokenBase64 =
                                          btoa(refreshToken);
                                      window.open(
                                          `${teleopApp}/devices/${deviceId}?refreshToken=${refreshTokenBase64}`,
                                          "_blank",
                                      );
                                  }
                              },
                          },
                      ]
                    : []),
            ],
            resource: "teleop",
            requiredAccess: "operator",
            disabled: !deviceIsTeleopable(deviceId) || !isObserve,
        });
    }

    private onNavigateToTeleop = async () => {
        const { deviceId } = this.props;
        const { navigate, routeTo } = routerStore;
        await navigate(routeTo.teleop({ id: deviceId }));
    };

    private fleetSettings(group?: number): IMenuItem<string>[] {
        if (!organizationStore.explorationsEnabled()) {
            return [];
        }
        return this.restrictedList.new({
            items: [
                {
                    // TODO: rename this to "Settings..." once we can do everything on the device page itself
                    value: "Workflow",
                    icon: "settings",
                    group,
                    onSelected: this.onShowFleetSettings,
                },
            ],
            resource: "streams",
            requiredAccess: "administrator",
        });
    }

    private onShowFleetSettings = () => {
        observeStore.showFleetSettings = true;
    };

    private shareItem(group?: number): IMenuItem<string>[] {
        return this.restrictedList.new({
            items: [
                {
                    value: "Share data",
                    icon: "share",
                    group,
                    onSelected: this.onShare,
                    hotkey: "shift+s",
                },
            ],
            resource: "share",
            requiredAccess: "operator",
            requiredFeature: "share",
            disabled: this.isOperate,
        });
    }

    private onShare() {
        track("Share started");
        appStore.sharing = true;
    }

    private exportItem(group?: number): IMenuItem<string>[] {
        return this.restrictedList.new({
            items: [
                {
                    icon: "download",
                    value: "Export",
                    group,
                    onSelected: this.onExportTelemetry,
                    hotkey: "shift+e",
                },
            ],
            resource: "streams",
            requiredAccess: "viewer",
            requiredFeature: "dataExport",
            disabled: !this.props.filter,
        });
    }

    private onExportTelemetry = (): void => {
        const { filter } = this.props;
        const [name] = observeStore.focusedStream || [];
        const names = (name && [name]) || undefined;
        observeStore.exportTelemetry({
            ...filter,
            names,
        });
    };

    private terminalItem(group?: number): IMenuItem<string>[] {
        const { session, usingTerminal } = operateStore;
        return this.restrictedList.new({
            items: this.isOperate
                ? [
                      {
                          icon: "command",
                          value: usingTerminal
                              ? "Close terminal"
                              : "Open terminal",
                          onSelected: this.onToggleTerminal,
                          group,
                          hotkey: "~",
                      },
                  ]
                : [],
            resource: "terminal",
            requiredAccess: "operator",
            requiredFeature: "ssh",
            disabled:
                !terminalEnabled ||
                !session?.deviceConfiguration?.document.terminalAccess,
        });
    }

    private fullscreenItem(group?: number): IMenuItem<string>[] {
        return [
            {
                icon: "fullscreen",
                value: "Fullscreen",
                onSelected: this.props.onFullscreen,
                group,
            },
        ];
    }

    private toggleFiltersItem(group?: number): IMenuItem<string>[] {
        return [
            {
                icon: "filter",
                value: `${this.props.showingFilters ? "Hide" : "Show"} filters`,
                onSelected: this.props.onToggleFilters,
                group,
            },
        ];
    }

    private onToggleTerminal = () => {
        operateStore.usingTerminal = !operateStore.usingTerminal;
    };

    private recordingItem(group?: number): IMenuItem<string>[] {
        const { isRecording } = screenRecordingStore;
        return recordVideoEnabled && this.isOperate
            ? [
                  isRecording
                      ? {
                            icon: "stop",
                            value: "Stop recording video",
                            onSelected: this.onStopRecordingVideo,
                            group,
                        }
                      : {
                            icon: "video",
                            value: "Record video",
                            onSelected: this.onStartRecordingVideo,
                            group,
                        },
              ]
            : [];
    }

    private onStartRecordingVideo = async () => {
        const device = deviceStore.get(this.props.deviceId);
        const name = (device || undefined)?.name;
        if (!name) {
            return;
        }
        const startRecordTime = formatDateTimeLocaleISO(new Date());
        const fileName = `${name}-${startRecordTime}`;
        await screenRecordingStore.startRecordingVideo(fileName);
    };

    private onStopRecordingVideo = () => {
        screenRecordingStore.stopRecordingVideo();
    };

    private fitFillItem(group?: number): IMenuItem<string>[] {
        if (!this.isOperate) {
            return [];
        }
        return (operateStore.canFill ?? true)
            ? (operateStore.fill ?? operateStore.shouldAutoFill)
                ? [
                      {
                          icon: "collapse",
                          value: "Fit image",
                          onSelected: this.onSetFitMode,
                          group,
                      },
                  ]
                : [
                      {
                          icon: "expand",
                          value: "Expand image",
                          onSelected: this.onSetFillMode,
                          group,
                      },
                  ]
            : [];
    }

    private onSetFillMode = () => {
        operateStore.fill = true;
    };

    private onSetFitMode = () => {
        operateStore.fill = false;
    };

    private metricsItem(group?: number): IMenuItem<string>[] {
        return this.isOperate
            ? [
                  {
                      icon: "line",
                      value: "Show teleop statistics",
                      onSelected: this.onOpenMetrics,
                      group,
                  },
              ]
            : [];
    }

    private teleopPrimarySwap(group?: number): IMenuItem<string>[] {
        return this.isOperate
            ? [
                  {
                      icon: "clone",
                      value: "Toggle primary view swap option",
                      onSelected: this.onTeleopPrimarySwap,
                      group,
                  },
              ]
            : [];
    }

    private shareFeedbackItem(group?: number): IMenuItem<string>[] {
        return this.isOperate
            ? [
                  {
                      icon: "warning",
                      value: "Share feedback",
                      onSelected: this.onOpenFeedback,
                      group,
                  },
              ]
            : [];
    }

    private onOpenMetrics = () => {
        operateStore.metricsVisible = true;
    };

    private onOpenFeedback = () => {
        operateStore.feedbackVisible = true;
    };

    private onTeleopPrimarySwap = () => {
        operateStore.teleopPrimarySwapIndex =
            (operateStore.teleopPrimarySwapIndex + 1) % 2;
    };
}
